/**
 * Auth slice — Phase D foundation (May 6, 2026).
 *
 * Extracted from `store/useStore.js` as a pure structural split.
 *
 * Contract preservation rules (zero regression):
 *   1. Every field name and action name MUST stay byte-identical to the
 *      previous monolithic store. Selectors like `useStore((s) => s.signIn)`
 *      MUST keep working unchanged.
 *   2. Persist shape MUST stay unchanged — auth fields were NOT in
 *      `partialize` before (token lives in localStorage directly), and
 *      MUST stay out.
 *   3. Side-effects (localStorage writes, `authChanged` event, error
 *      handling) MUST match exactly.
 *
 * The slice is a creator: `createAuthSlice(set, get, { apiCall })` returns
 * the auth fields and actions, which are then spread into the root store
 * object inside `useStore.js`.
 */

export const createAuthSlice = (set, get, { apiCall }) => ({
  // ----- state -----
  currentUser: null,
  authToken: null,
  authModal: null,
  authLoading: false,
  authError: null,

  // ----- actions -----
  signIn: async (email, password) => {
    set({ authLoading: true, authError: null });
    try {
      const data = await apiCall('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      localStorage.setItem('eoc-auth-token', data.token);
      try {
        localStorage.setItem('eoc-current-user', JSON.stringify(data.user));
      } catch (err) { console.warn('[auth] cache user failed', err); }

      set({
        currentUser: data.user,
        authToken: data.token,
        authModal: null,
        authLoading: false,
      });
      try {
        window.dispatchEvent(new Event('authChanged'));
      } catch (err) { console.warn('[auth] dispatch failed', err); }
      return { success: true };
    } catch (error) {
      set({ authError: error.message, authLoading: false });
      return { success: false, error: error.message };
    }
  },

  signUp: async (email, password, displayName) => {
    set({ authLoading: true, authError: null });
    try {
      const data = await apiCall('/api/auth/signup', {
        method: 'POST',
        body: JSON.stringify({ email, password, displayName }),
      });

      localStorage.setItem('eoc-auth-token', data.token);
      try {
        localStorage.setItem('eoc-current-user', JSON.stringify(data.user));
      } catch (err) { console.warn('[auth] cache user failed', err); }

      set({
        currentUser: data.user,
        authToken: data.token,
        authModal: null,
        authLoading: false,
      });
      try {
        window.dispatchEvent(new Event('authChanged'));
      } catch (err) { console.warn('[auth] dispatch failed', err); }
      return { success: true };
    } catch (error) {
      set({ authError: error.message, authLoading: false });
      return { success: false, error: error.message };
    }
  },

  signOut: () => {
    localStorage.removeItem('eoc-auth-token');
    localStorage.removeItem('eoc-current-user');
    set({ currentUser: null, authToken: null, savedProjects: [] });
    try {
      window.dispatchEvent(new Event('authChanged'));
    } catch (err) { console.warn('[auth] dispatch failed', err); }
  },

  setAuthModal: (modal) => set({ authModal: modal, authError: null }),
  clearAuthError: () => set({ authError: null }),
  setCurrentUser: (user) => {
    set({ currentUser: user });
    try {
      if (user) {
        localStorage.setItem('eoc-current-user', JSON.stringify(user));
      } else {
        localStorage.removeItem('eoc-current-user');
      }
      window.dispatchEvent(new Event('authChanged'));
    } catch (err) { console.warn('[auth] setCurrentUser side-effect failed', err); }
  },
});
